import { useCallback, useEffect, useRef } from "react";

import { type Input } from "antd";
import { Key } from "ts-key-enum";

import {
  BuiInput,
  type IBuiInputProps,
} from "@btnet/blueprint-components/BuiInput";
import { cn } from "@btnet/utilities-core";

import {
  BTAnalyticsEvent,
  BTTrack,
  type IBTTrackingProps,
  trackBTEvent,
} from "../analytics/analytics";
import {
  type IInputDebouncingContext,
  useDebouncedOnChange,
} from "../hooks/UseDebouncedOnChange";
import { resolveHotkey, type THotkey } from "../Hotkey/hotkey.types";
import { useHotkey } from "../Hotkey/useHotkey";
import { type KeyOfOrString } from "../types/KeyOfOrString.types";
import { ValueDisplay } from "../ValueDisplay/ValueDisplay";

export interface IBTInputProps<FormValues>
  extends Omit<
    IBuiInputProps,
    "id" | "onChange" | "onBlur" | "value" | "maxLength" | "defaultValue"
  > {
  id: KeyOfOrString<FormValues>;
  "data-testid": string;
  readOnly?: boolean;

  /** pass formik setFieldValue */

  onChange: (field: KeyOfOrString<FormValues>, value: string) => void;

  /** pass formik setFieldTouched */

  onBlur?: (
    id: KeyOfOrString<FormValues>,
    touched: boolean,
    value: string | number | undefined,
  ) => void;

  value?: string | number | undefined;

  defaultValue?: string | number;

  fieldRef?: React.RefObject<Input>;

  autoFocus?: boolean;

  /**
   * Sets the debounce context and limits. Should only be set in edge cases.
   */
  debouncingContext?: IInputDebouncingContext;

  /**
   * Disables default behavior of attempting to submit the form when pressing the enter key inside of the input.
   * (eg: Line Item container inputs should not submit the form but set the line item to read only when pressing the enter key)
   */
  disableFormSubmit?: boolean;

  hotkey?: THotkey;
}

/**
 * The BTInput component is a wrapper around Ant Design's Input component.
 * It provides additional features such as debounced input handling, tracking, and hotkey support.
 * It is expected that BTInput will be used in a BTFormItem because BTFormItem formats the display to play nicely with other form items.
 *
 * When `readOnly` is set, it renders a BdsValueDisplay instead of an editable input.
 * Pressing Enter inside a form submits that form unless `disableFormSubmit` is set.
 * @example
 * <BTInput
 *   id="firstName"
 *   data-testid="firstName"
 *   value={values.firstName}
 *   onChange={setFieldValue}
 *   onBlur={setFieldTouched}
 * />
 */
export const BTInput = BTTrack({ element: "BTInput" })(function <
  FormValues = undefined,
>({
  id,
  value,
  readOnly,
  onChange,
  onFocus,
  onBlur = () => {},
  fieldRef,
  autoFocus,
  addonBefore,
  addonAfter,
  "data-testid": testid,
  defaultValue,
  placeholder,
  debouncingContext,
  disableFormSubmit,
  hotkey,
  tracking,
  trackingEvent,
  className,
  type,
  ...otherProps
}: IBTInputProps<FormValues> & IBTTrackingProps) {
  const { debouncedValue, debouncedChange, flush } = useDebouncedOnChange<
    typeof value,
    FormValues
  >(value ?? defaultValue ?? "", onChange, debouncingContext);
  const refInternal = useRef<Input>(null);
  const ref = fieldRef ?? refInternal;
  const hotkeyRef = useRef<HTMLElement>();

  // Core's useHotkey takes no element, so the ctrl+/ overlay no longer anchors a tooltip to
  // this input — the key is still listed, just without the positioned bubble.
  const resolvedHotkey = resolveHotkey(hotkey, id as string);
  useHotkey({
    command: resolvedHotkey?.command ?? "",
    friendlyName: resolvedHotkey?.friendlyName,
    onCommand: () => triggerFocus(),
    disabled: readOnly || !resolvedHotkey,
  });

  function onChangeWrapper(event: React.ChangeEvent<HTMLInputElement>) {
    debouncedChange(id, event.target.value);
  }

  function onBlurWrapper() {
    flush();
    onBlur(id, true, debouncedValue);
    trackBTEvent(tracking, {
      element: "BTInput",
      event: trackingEvent ?? BTAnalyticsEvent.InputBlur,
      testid,
    });
  }

  function onFocusWrapper(event: React.FocusEvent<HTMLInputElement>) {
    if (onFocus) {
      onFocus(event);
    }
    trackBTEvent(tracking, {
      element: "BTInput",
      event: trackingEvent ?? BTAnalyticsEvent.InputFocus,
      testid,
    });
  }

  const triggerFocus = useCallback(() => {
    ref.current?.focus();
  }, [ref]);

  useEffect(() => {
    hotkeyRef.current = ref.current?.input;
    if (autoFocus) {
      triggerFocus();
    }
  }, [autoFocus, ref, triggerFocus]);

  if (readOnly) {
    return (
      <ValueDisplay
        id={id as string}
        data-testid={testid}
        value={debouncedValue}
        {...otherProps}
      />
    );
  }

  // Handles input fields when the user uses the Enter key to submit a form
  function handleInputDebounceOnKeyDown(
    event: React.KeyboardEvent<HTMLInputElement>,
  ) {
    // Note: Ignore if CTRL is pressed as that conflicts with the saveAndClose hotkey
    if (
      event.key === Key.Enter &&
      !event.ctrlKey &&
      !disableFormSubmit &&
      event.target &&
      !event.repeat
    ) {
      const target = event.target as HTMLInputElement;
      const form = target.closest("form");
      if (form) {
        event.preventDefault();
        onChange(id, target.value);
        requestAnimationFrame(() => {
          if (typeof form.requestSubmit === "function") {
            form.requestSubmit();
          } else {
            form.dispatchEvent(new Event("submit", { cancelable: true }));
          }
        });
      }
    }
  }

  const input = (
    <BuiInput
      id={id as string}
      className={cn(className, "BTInput")}
      data-testid={testid}
      value={debouncedValue}
      defaultValue={defaultValue}
      placeholder={placeholder}
      aria-disabled={otherProps.disabled}
      disabled={otherProps.disabled}
      onChange={onChangeWrapper}
      onBlur={onBlurWrapper}
      ref={fieldRef ?? refInternal}
      onKeyDown={handleInputDebounceOnKeyDown}
      addonBefore={addonBefore}
      addonAfter={addonAfter}
      onFocus={onFocusWrapper}
      {...(type !== undefined && { type })}
      {...otherProps}
    />
  );

  return input;
});
